<!--  Toolbar (main navigation) -->
<mat-toolbar class="toolbar" role="navigation">
    <!-- clickable logo -->
    <a class="toolbar__logo-wrapper" routerLink="/" aria-label="Homepage" i18n-aria-label="@@toolbar-link-homepage">
        <img class="toolbar__logo" alt="Scope Logo" src="assets/icons/scope-logo.svg" />
        <span>Scope Hub</span>
    </a>

    <!-- spacer -->
    <span class="toolbar__spacer"></span>

    @if (showStyleGuide) {
        <a
            mat-icon-button
            class="riege-button"
            routerLink="/styleguide"
            aria-label="Styleguide"
            i18n-aria-label="@@menu-styleguide"
            data-testid="toolbar-button-styleguide"
            matTooltip="Styleguide"
            matTooltipPosition="below">
            <mat-icon class="riege-icon">line_style</mat-icon>
        </a>
    }

    <!-- ══════════════════════════════════════════════════════════════
         Launch Scope — only on desktop, only when logged in
         Mirrors the launch logic from ScopeCardComponent
    ═══════════════════════════════════════════════════════════════ -->
    @if (!isMobile() && !isTabletPortrait() && authService.isLoggedIn()) {
        <!-- Server selector — only shown when the user has more than one server -->
        @if (servers()!.length > 1) {
            <mat-form-field subscriptSizing="dynamic">
                <mat-label i18n="@@toolbar-launch-server-label">Server</mat-label>
                <mat-select [value]="selectedServer()" (valueChange)="authService.selectedServer.set($event)">
                    @for (server of servers(); track $index) {
                        <mat-option [value]="server">{{ server.scopeDomain }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>
        }

        <button
            mat-flat-button
            class="riege-button riege-button--inversed"
            data-testid="toolbar-button-launch-scope"
            [disabled]="!selectedServer()"
            (click)="launchScope()"
            i18n="@@button-label-launch-scope"
            >Launch Scope</button
        >
    }
    <!-- ══ END Launch Scope ══════════════════════════════════════════ -->

    <!-- Help / Status -->
    <button
        mat-icon-button
        class="riege-button toolbar__trigger"
        (menuOpened)="getStatus(); onMenuOpened(helpTrigger)"
        [matMenuTriggerFor]="helpMenu"
        #helpTrigger="matMenuTrigger"
        aria-label="Help menu"
        i18n-aria-label="@@menu-help"
        aria-haspopup="true"
        [attr.aria-expanded]="helpMenu.closed"
        data-testid="toolbar-button-help">
        <mat-icon class="riege-icon">help_outline</mat-icon>
    </button>

    <mat-menu #helpMenu="matMenu" xPosition="before" [hasBackdrop]="false" class="riege-menu">
        <div>
            <a mat-menu-item tabindex="0" href="https://status.riege.com/" target="_blank">
                <span i18n="@@menu-item-status-page">System status</span>
                <span class="toolbar__status-indicator">
                    @if (indicator()) {
                        <mat-icon class="riege-icon" [ngClass]="[color(), 'md-20']">{{ indicator() }}</mat-icon>
                    }
                    <span class="visually-hidden">{{ status() }}</span>
                </span>
            </a>
            <a mat-menu-item tabindex="0" href="{{ linkKnowledgeBase }}" target="_blank" i18n="@@menu-item-manual-link"
                >Knowledge base</a
            >
            @if (authService.isLoggedIn()) {
                <a mat-menu-item tabindex="0" href="mailto:service@riege.com" i18n="@@menu-item-support-link"
                    >Contact support</a
                >
            }
        </div>
    </mat-menu>

    <!-- Profile / Login -->
    @if (!authService.isLoggedIn()) {
        <button
            mat-flat-button
            id="login"
            data-testid="toolbar-button-login"
            class="riege-button"
            [disabled]="authService.loginDisabled()"
            debounceClick
            (doClick)="login()"
            [debounceTime]="700"
            i18n="@@button-label-login"
            >Log in
        </button>
    } @else {
        @defer {
            <a
                mat-icon-button
                class="riege-button is-logged-in toolbar__trigger"
                tabindex="0"
                [matMenuTriggerFor]="profileMenu"
                #menuTriggerProfile="matMenuTrigger"
                aria-label="User menu"
                i18n-aria-label="@@menu-user"
                aria-haspopup="true"
                [attr.aria-expanded]="profileMenu.closed"
                data-testid="toolbar-menu"
                (keydown.enter)="menuTriggerProfile.openMenu()"
                (menuOpened)="onMenuOpened(menuTriggerProfile)">
                <span class="toolbar__avatar">
                    <mat-icon class="riege-icon {{ avatar() }}" svgIcon="{{ avatar() }}" aria-hidden="true"></mat-icon>
                </span>
            </a>

            <mat-menu #profileMenu="matMenu" xPosition="before" [hasBackdrop]="false" class="riege-menu">
                <div>
                    <div class="riege-button toolbar__profile-menu" mat-menu-item>
                        <mat-icon
                            class="riege-icon {{ avatar() }}"
                            svgIcon="{{ avatar() }}"
                            aria-hidden="true"></mat-icon>
                        <span class="toolbar__profile-user-name">{{ currentUser().name }}</span>
                        <br />
                        <a
                            class="toolbar__profile-company-link"
                            (click)="$event.stopPropagation()"
                            [routerLink]="['/', 'scope-community', 'community-members', currentUser().communityId]">
                            {{ organizationLabel() }}
                        </a>
                    </div>

                    <mat-divider class="reset-margin--all"></mat-divider>

                    <a
                        mat-menu-item
                        tabindex="0"
                        routerLink="/user-account/profile"
                        i18n="@@toolbar-menu-profile-link"
                        routerLinkActive="active"
                        #rla="routerLinkActive"
                        ariaCurrentWhenActive="page"
                        data-testid="btn-user-profile"
                        >User profile
                    </a>
                    <a
                        mat-menu-item
                        tabindex="0"
                        [routerLink]="['/user-account/scope-settings']"
                        i18n="@@toolbar-menu-scope-settings"
                        routerLinkActive="active"
                        #rlaOrg="routerLinkActive"
                        ariaCurrentWhenActive="page"
                        data-testid="btn-scope-settings"
                        >Scope settings
                    </a>

                    @if (switchTenants) {
                        <a
                            mat-menu-item
                            tabindex="0"
                            class="not-dimmed"
                            data-testid="btn-switch-workspace"
                            aria-haspopup="true"
                            [matMenuTriggerFor]="workspaceMenu"
                            [attr.aria-expanded]="workspaceMenu.closed"
                            i18n="@@topbar-menu-switch-workspace"
                            >Switch workspace
                        </a>
                        <mat-menu #workspaceMenu="matMenu" xPosition="before" class="riege-menu">
                            <button mat-menu-item (click)="switchTenant('tenant')" data-testid="btn-organization">
                                {{ organizationLabel() }}
                            </button>
                        </mat-menu>
                    }

                    <mat-divider class="reset-margin--all"></mat-divider>

                    <button mat-menu-item id="logout" (click)="logout()" i18n="@@topbar-menu-logout-link"
                        >Log out</button
                    >
                </div>
            </mat-menu>
        }
    }
</mat-toolbar>
